import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import projectService from '../services/projectService';
import { Project, CreateProjectData, UpdateProjectData } from '../types/project.types';

interface NewProjectDialogOptions {
  onLimitError?: () => void;
  onCreated?: (project: Project) => void;
}

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  error: string | null;
  showNewProjectDialog: boolean;
  newProjectDialogOptions: NewProjectDialogOptions;
  createProject: (data: CreateProjectData) => Promise<Project>;
  updateProject: (projectId: string, data: UpdateProjectData) => Promise<Project>;
  deleteProject: (projectId: string) => Promise<void>;
  fetchProjects: (includeArchived?: boolean) => Promise<void>;
  selectProject: (project: Project | null) => void;
  openNewProjectDialog: (opts?: NewProjectDialogOptions) => void;
  closeNewProjectDialog: () => void;
  attachConversationToProject: (projectId: string, conversationId: string) => Promise<void>;
  detachConversationFromProject: (projectId: string, conversationId: string) => Promise<void>;
  archiveProject: (projectId: string) => Promise<void>;
  unarchiveProject: (projectId: string) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function useProjects() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
}

interface ProjectProviderProps {
  children: React.ReactNode;
}

export function ProjectProvider({ children }: ProjectProviderProps) {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectDialogOptions, setNewProjectDialogOptions] = useState<NewProjectDialogOptions>({});

  const fetchProjects = useCallback(async (includeArchived = false) => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);
    try {
      const fetchedProjects = await projectService.getProjects(includeArchived);
      setProjects(fetchedProjects);
      
      // If current project was deleted or archived, clear selection
      if (currentProject && !fetchedProjects.find(p => p.id === currentProject.id)) {
        setCurrentProject(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch projects');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentProject]);

  const createProject = async (data: CreateProjectData): Promise<Project> => {
    setError(null);
    try {
      const newProject = await projectService.createProject(data);
      setProjects(prev => [newProject, ...prev]);
      return newProject;
    } catch (err: any) {
      setError(err.message || 'Failed to create project');
      throw err;
    }
  };

  const openNewProjectDialog = (opts: NewProjectDialogOptions = {}) => {
    setNewProjectDialogOptions(opts);
    setShowNewProjectDialog(true);
  };

  const closeNewProjectDialog = () => {
    setShowNewProjectDialog(false);
    setNewProjectDialogOptions({});
  };

  const updateProject = async (projectId: string, data: UpdateProjectData): Promise<Project> => {
    setError(null);
    try {
      const updatedProject = await projectService.updateProject(projectId, data);
      setProjects(prev => prev.map(p => p.id === projectId ? updatedProject : p));
      
      if (currentProject?.id === projectId) {
        setCurrentProject(updatedProject);
      }
      
      return updatedProject;
    } catch (err: any) {
      setError(err.message || 'Failed to update project');
      throw err;
    }
  };

  const deleteProject = async (projectId: string): Promise<void> => {
    setError(null);
    try {
      await projectService.deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));

      // Clear cached brain data for the deleted project
      localStorage.removeItem(`signals_${projectId}`);
      localStorage.removeItem(`sources_${projectId}`);
      localStorage.removeItem(`primer_${projectId}`);

      if (currentProject?.id === projectId) {
        setCurrentProject(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
      throw err;
    }
  };

  const selectProject = (project: Project | null) => {
    setCurrentProject(project);
    if (project) {
      localStorage.setItem('selectedProjectId', project.id);
    } else {
      localStorage.removeItem('selectedProjectId');
    }
  };

  const attachConversationToProject = async (projectId: string, conversationId: string) => {
    setError(null);
    try {
      await projectService.attachConversation(projectId, conversationId);
      setProjects(prev => prev.map(p => 
        p.id === projectId 
          ? { ...p, conversationCount: p.conversationCount + 1 }
          : p
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to attach conversation');
      throw err;
    }
  };

  const detachConversationFromProject = async (projectId: string, conversationId: string) => {
    setError(null);
    try {
      await projectService.detachConversation(projectId, conversationId);
      // Update conversation count
      setProjects(prev => prev.map(p => 
        p.id === projectId 
          ? { ...p, conversationCount: Math.max(0, p.conversationCount - 1) }
          : p
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to detach conversation');
      throw err;
    }
  };

  const archiveProject = async (projectId: string) => {
    await updateProject(projectId, { isArchived: true });
  };

  const unarchiveProject = async (projectId: string) => {
    await updateProject(projectId, { isArchived: false });
  };

  useEffect(() => {
    if (currentUser) {
      fetchProjects();
    } else {
      setProjects([]);
      setCurrentProject(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && projects.length > 0) {
      const selectedProjectId = localStorage.getItem('selectedProjectId');
      if (selectedProjectId) {
        const project = projects.find(p => p.id === selectedProjectId);
        if (project) {
          setCurrentProject(project);
        }
      }
    }
  }, [currentUser, projects]);

  const value = {
    projects,
    currentProject,
    loading,
    error,
    showNewProjectDialog,
    newProjectDialogOptions,
    createProject,
    updateProject,
    deleteProject,
    fetchProjects,
    selectProject,
    openNewProjectDialog,
    closeNewProjectDialog,
    attachConversationToProject,
    detachConversationFromProject,
    archiveProject,
    unarchiveProject
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}